import { type ReactNode } from "react"
import { Vertical } from "@/components/standard/BFLayout"
import BFText, { H5, Small } from "@/components/standard/BFText"
import { type SubmitBehavior, type TextInputProps, View } from "react-native"
import { BFTextInput } from "@/components/standard/BFTextInput"
import { type Mask } from "react-native-mask-input"
import { useInputFieldData } from "@/hooks/useInputFieldData"
import FieldErrorIcon from "@/assets/icons/field-error.svg"

export const FieldWithLabel = ({
  label,
  descriptor,
  description,
  required = false,
  showRequiredIndicator = true, // To toggle required/optional indicator
  children,
  error,
  dark = false,
}: {
  dark?: boolean
  label: string
  descriptor?: string
  description?: string
  required?: boolean
  showRequiredIndicator?: boolean // Controls display of * or (optional)
  children: ReactNode
  error?: string | null
}) => {
  return (
    <Vertical>
      <H5 className={dark ? "text-off-white" : "text-midnight-blue"}>
        {label}
        {descriptor && <Small className="text-warm-grey"> {descriptor}</Small>}
        {showRequiredIndicator &&
          (required ? (
            <BFText className={dark ? "text-salmon" : "text-red-orange"}>
              {" "}
              *
            </BFText>
          ) : (
            <Small className="text-warm-grey"> (optional)</Small>
          ))}
      </H5>

      {description && (
        <Small
          className={`${dark ? "text-off-white" : "text-midnight-blue"} mb-1`}
        >
          {description}
        </Small>
      )}
      <View>{children}</View>
      {error && (
        <Small className={dark ? "text-salmon" : "text-red-orange"}>
          {error}
        </Small>
      )}
    </Vertical>
  )
}

export const TextFieldWithLabel = ({
  label,
  descriptor,
  description,
  required = false,
  showRequiredIndicator = true,
  textFieldProps,
  submitBehavior,
  initialValue,
  mask,
  validator,
  onValueChange,
  dark = false,
  multiline = false, // New support for multiline input
  containerClassName,
}: {
  textFieldProps?: TextInputProps
  label: string
  descriptor?: string
  description?: string
  required?: boolean
  showRequiredIndicator?: boolean // Controls display of * or (optional)
  submitBehavior?: SubmitBehavior
  initialValue?: string
  mask?: Mask
  dark?: boolean
  validator?: (value: string) => string | undefined
  onValueChange?: (value: string, error?: string) => void
  multiline?: boolean
  containerClassName?: string
}) => {
  const { props, error } = useInputFieldData({
    initialValue,
    mask,
    validator,
    onValueChange: (value, error1) => onValueChange?.(value, error1),
  })

  return (
    <FieldWithLabel
      label={label}
      descriptor={descriptor}
      description={description}
      required={required}
      showRequiredIndicator={showRequiredIndicator}
      error={error}
      dark={dark}
    >
      <BFTextInput
        {...props}
        containerClassName={containerClassName}
        variant={
          error
            ? dark
              ? "formErrorDark"
              : "formError"
            : dark
              ? "formDark"
              : "form"
        }
        {...textFieldProps}
        multiline={multiline} // Enable multiline when true
        numberOfLines={multiline ? 4 : undefined} // Adjust height for multiline
        style={[textFieldProps?.style, multiline && { minHeight: 100 }]} // Increase height for multiline
        accessoryRight={error ? <FieldErrorIcon /> : undefined}
        {...(submitBehavior !== undefined ? { submitBehavior } : {})}
      />
    </FieldWithLabel>
  )
}
